class DOMUtils {

    static debug = true; 

    static removeEventListenerFromAllElementsWithClassName(className, eventType, handler) {
        const elements = document.getElementsByClassName(className);
        Array.from(elements).forEach(element => {
            element.removeEventListener(eventType, handler);
        });
    }

    static createElement({ tagName, properties = {}, children = [] }) { // Added children parameter
        if (!tagName || typeof tagName !== 'string') {
            console.error('Invalid tagName: Must be a non-empty string.');
            return null; // Return null for invalid tagName
        }

        try {
            // Create the element
            const element = document.createElement(tagName);

            // Set attributes from properties
            const { attributes = {}, innerText = '', events = {}, style = {} } = properties; // Added style

            // Set attributes
            for (const [key, value] of Object.entries(attributes)) {
                if (key === 'class') {
                    element.className = value; // Set className for classes
                } else {
                    element.setAttribute(key, value); // Set other attributes
                }
            }

            // Set innerText as a property if provided
            if (innerText) {
                element.innerText = innerText; // Set innerText (not an attribute)
            }

            // Set styles if provided
            Object.keys(style).forEach(styleName => { // Added style handling
                element.style[styleName] = style[styleName];
            });

            // Add event listeners if provided
            for (const [eventType, callback] of Object.entries(events)) {
                element.addEventListener(eventType, callback); // Add event listeners
            }

            // Append children if provided
            children.forEach(child => { // Added children handling
                if (child instanceof Node) {
                    element.appendChild(child);
                }
            });

            return element; // Return the created element
        } catch (error) {
            console.error('Error creating element:', error);
            return null; // Return null in case of an error
        }
    }

    static toggleStyleVisibility(element) {
        if (!element) {
            console.warn('DOMUtils.toggleVisibility: Element is null or undefined');
            return;
        }

        // Check if the element is currently visible
        if (this.isElementVisible(element)) { // Use class name to call static method
            this.hideElement(element); // Hide the element if it's visible
        } else {
            this.showElement(element); // Show the element if it's hidden
            console.log('Show');
        }
    }

    static checkElementPosition(index, length) {
        return {
            isFirstElement: index === 0,
            isLastElement: index === length - 1
        };
    }

    static addEventListenerToElement(element, eventType, callback) {
        // Check if the element is valid
        if (element instanceof HTMLElement) {
            element.addEventListener(eventType, (event) => {
                // Optional: Log the event or perform additional actions
                console.log(`Event '${eventType}' triggered on`, element);
                callback(event); // Call the provided callback function
            });
        } else {
            console.warn('Invalid element provided. Please provide a valid DOM element.');
        }
    }

    static addClickListener(selector, callback) {
        try {
            const elements = document.querySelectorAll(selector);
            if (elements.length > 0) {
                elements.forEach(element => {
                    element.addEventListener('click', () => {
                        callback(element, elements); // Pass the clicked element and the list of elements
                    });
                });
            } else {
                console.warn(`DOMUtils.addClickListener: No elements found for selector "${selector}"`);
            }
        } catch (error) {
            console.error('DOMUtils.addClickListener: Error while adding click listener', error);
        }
    }

    static addEventListenerToAllElementsWithClassName(className, eventType, callback) {
        try {
            const elements = document.getElementsByClassName(className);
            if (elements.length > 0) {
                Array.from(elements).forEach(element => {
                    element.addEventListener(eventType, (event) => {
                        console.log(`Event '${eventType}' triggered on`, element);
                        callback(event); // Call the provided callback function
                    });
                });
            } else {
                console.warn(`DOMUtils.addEventListenerToAllElementsWithClass: No elements found for class "${className}"`);
            }
        } catch (error) {
            console.error('DOMUtils.addEventListenerToAllElementsWithClass: Error while adding event listener', error);
        }
    }

    static addClassToParent(element, classToAdd) {
        if (this.debug) { // Check if debugger is true
            console.log('DOMUtils.addClassToParent: Attempting to add class to parent element', { element, classToAdd }); // Log the attempt with parameters
        }
        try {
            if (element && element.parentElement) {
                console.log('Parent element found:', element.parentElement); // Log the parent element
                element.parentElement.classList.add(classToAdd);
                console.log(`Class "${classToAdd}" added to parent element.`); // Log success
            } else {
                console.warn('DOMUtils.addClassToParent: Element or its parent is null or undefined');
            }
        } catch (error) {
            console.error('DOMUtils.addClassToParent: Failed to add class to parent element', error);
        }
    }

    static removeClassFromParentElement(element, classToRemove) {
        try {
            if (element && element.parentElement) {
                element.parentElement.classList.remove(classToRemove);
            } else {
                console.warn('DOMUtils.removeClassFromParent: Element or its parent is null or undefined');
            }
        } catch (error) {
            console.error('DOMUtils.removeClassFromParent: Failed to remove class from parent element', error);
        }
    }

    static removeClassFromAllElementsBySelector(selector, className) {
        if (this.debug) { // Check if debugger is true
            console.log('removeClassFromElements function fired with selector:', selector, 'and className:', className);
        }
        try {
            const elements = this.getElements(selector);
            elements.forEach(element => {
                if (this.debug) { // Check if debugger is true
                    console.log('Found element:', element); // Log the element found
                }
                this.removeClass(element, className);
            });
        } catch (error) {
            console.error('DOMUtils.removeClassFromElements: Error while removing class from elements', error);
        }
    }

    static removeClassFromAllElementsByClassName(targetClassName, classToRemove) { // Updated method signature
        if (this.debug) { // Check if debugger is true
            console.log('removeClassFromAllElementsByClassName function fired with targetClassName:', targetClassName, 'and classToRemove:', classToRemove); // Updated log message
        }
        try {
            const elements = document.getElementsByClassName(targetClassName); // Get elements by target class name
            Array.from(elements).forEach(element => { // Convert HTMLCollection to array
                if (this.debug) { // Check if debugger is true
                    console.log('Found element:', element); // Log the element found
                }
                this.removeClass(element, classToRemove); // Remove the specified class from each element
            });
        } catch (error) {
            console.error('DOMUtils.removeClassFromAllElementsByClassName: Error while removing class from elements', error);
        }
    }


    static removeClassFromAllElements(elements, className) { // Changed parameter from selector to elements
        if (this.debug) { // Check if debugger is true
            console.log('removeClassFromElements function fired with elements:', elements, 'and className:', className); // Updated log message
        }
        try {
            if (!Array.isArray(elements)) { // Check if elements is an array
                console.warn('DOMUtils.removeClassFromAllElements: elements should be an array');
                return; // Exit if not an array
            }
            elements.forEach(element => {
                if (this.debug) { // Check if debugger is true
                    console.log('Found element:', element); // Log the element found
                }
                this.removeClass(element, className);
            });
        } catch (error) {
            console.error('DOMUtils.removeClassFromElements: Error while removing class from elements', error);
        }
    }

    static getElement(selector) {
        // Validate the selector
        if (typeof selector !== 'string' || selector.trim() === '') {
            console.error("Invalid selector: Must be a non-empty string.");
            return null; // Return null for invalid selector
        }

        try {
            // Check if the selector is for an ID
            if (selector.startsWith('#')) {
                return document.getElementById(selector.slice(1)); // Remove `#` to get the ID
            }

            // Use querySelector for any other selectors (classes, tags, etc.)
            return document.querySelector(selector);

        } catch (error) {
            console.error("Error selecting element:", error.message);
            return null; // Return null in case of an error
        }
    }

    static getElements(selector) {
        // Validate the selector
        if (typeof selector !== 'string' || selector.trim() === '') {
            console.error("Invalid selector: Must be a non-empty string.");
            return []; // Return an empty array for invalid selector
        }

        try {
            // Use querySelectorAll for any selectors (includes class queries)
            return Array.from(document.querySelectorAll(selector)); // Convert NodeList to an array

        } catch (error) {
            console.error("Error selecting elements:", error.message);
            return []; // Return an empty array in case of an error
        }
    }

    static waitForElements(className, callback) {
        try {
            // Execute immediately if elements already exist
            const existingElements = document.getElementsByClassName(className);
            if (existingElements.length > 0) {
                if (this.debug) {
                    console.log(`Found existing elements for class "${className}":`, existingElements);
                }
                callback(existingElements);
                return;
            }

            // Otherwise, set up observer
            const observer = new MutationObserver(() => {
                const elements = document.getElementsByClassName(className);
                if (elements.length > 0) {
                    try {
                        if (this.debug) {
                            console.log(`Found new elements for class "${className}":`, elements);
                        }
                        callback(elements);
                    } catch (callbackError) {
                        console.error(`Error executing callback for "${className}":`, callbackError);
                    } finally {
                        observer.disconnect();
                    }
                }
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        } catch (error) {
            console.error(`DOMUtils.waitForElements: Error in waitForElements("${className}"):`, error);
        }
    }

    static waitForElement(selector, callback) {
        try {
            // Get selector type and clean it
            const isClass = selector.startsWith('.');
            const isId = selector.startsWith('#');
            const cleanSelector = isClass || isId ? selector.substring(1) : selector;

            // Check for existing element
            const existingElement = isClass ? document.getElementsByClassName(cleanSelector)[0]
                : isId ? document.getElementById(cleanSelector)
                : document.querySelector(selector);

            if (existingElement) {
                callback(existingElement);
                return;
            }

            // Watch for new elements
            const observer = new MutationObserver(() => {
                const element = isClass ? document.getElementsByClassName(cleanSelector)[0]
                    : isId ? document.getElementById(cleanSelector)
                    : document.querySelector(selector);

                if (element) {
                    callback(element);
                    observer.disconnect();
                }
            });

            observer.observe(document.body, { childList: true, subtree: true });
        } catch (error) {
            console.error(`DOMUtils.waitForElement: Error in waitForElement("${selector}"):`, error);
        }
    }


        // ... existing methods ...

    static waitForElementById(id, callback) {
        try {
            // Check for existing element
            const existingElement = document.getElementById(id);
            if (existingElement) {
                callback(existingElement);
                return;
            }

            // Watch for new elements
            const observer = new MutationObserver(() => {
                const element = document.getElementById(id);
                if (element) {
                    callback(element);
                    observer.disconnect();
                }
            });

            observer.observe(document.body, { childList: true, subtree: true });
        } catch (error) {
            console.error(`DOMUtils.waitForElementById: Error in waitForElementById("${id}"):`, error);
        }
    }

    static isElementVisible(element) {
        if (!element) {
            console.warn('DOMUtils.isElementVisible: Element is null or undefined');
            return false;
        }

        try {
            const displayStyle = getComputedStyle(element).display;
            return displayStyle !== 'none';
        } catch (error) {
            console.error('DOMUtils.isElementVisible: Failed to get computed style', error);
            return false;
        }
    }

    static hideElement(element) {
        if (!element) {
            console.warn('DOMUtils.hideElement: Element is null or undefined');
            return;
        }
        try {
            element.style.display = 'none';
        } catch (error) {
            console.error('DOMUtils.hideElement: Failed to hide element', error);
        }
    }

    static showElement(element) {
        if (!element) {
            console.warn('DOMUtils.showElement: Element is null or undefined');
            return;
        }
        try {
            element.style.display = 'block';
        } catch (error) {
            console.error('DOMUtils.showElement: Failed to show element', error);
        }
    }

    static hideElementWithId(id) {
        if (!id) {
            console.warn('DOMUtils.hideElementById: Id is null or undefined');
            return;
        }
        try {
            const element = document.getElementById(id);
            if (element) {
                this.hideElement(element);
            } else {
                console.warn(`DOMUtils.hideElementById: No element found with id "${id}"`);
            }
        } catch (error) {
            console.error(`DOMUtils.hideElementById: Error while hiding element with id "${id}":`, error);
        }
    }

    static showElementWithId(id) {
        if (!id) {
            console.warn('DOMUtils.showElementById: Id is null or undefined');
            return;
        }
        try {
            const element = document.getElementById(id);
            if (element) {
                element.style.display = ''; // Remove the display style to show the element
            } else {
                console.warn(`DOMUtils.showElementById: No element found with id "${id}"`);
            }
        } catch (error) {
            console.error(`DOMUtils.showElementById: Error while showing element with id "${id}":`, error);
        }
    }

    static hideElements(elements) {
        if (!elements) {
            console.warn('DOMUtils.hideElements: Elements array is null or undefined');
            return;
        }
        try {
            for (let element of elements) {
                this.hideElement(element);
            }
        } catch (error) {
            console.error('DOMUtils.hideElements: Failed to hide elements', error);
        }
    }

    static showElements(elements) {
        if (!elements) {
            console.warn('DOMUtils.showElements: Elements array is null or undefined');
            return;
        }
        try {
            for (let element of elements) {
                this.showElement(element);
            }
        } catch (error) {
            console.error('DOMUtils.showElements: Failed to show elements', error);
        }
    }

    static addClass(element, className) {
        if (!element) {
            console.warn('DOMUtils.addClass: Element is null or undefined');
            return;
        }
        if (!className) {
            console.warn('DOMUtils.addClass: className is null or undefined');
            return;
        }
        try {
            element.classList.add(className);
        } catch (error) {
            console.error('DOMUtils.addClass: Failed to add class', error);
        }
    }

    static removeDisplayFromElementBySelector(selector) { // Updated method name for clarity
        if (!selector) {
            console.warn('DOMUtils.removeDisplayFromElementBySelector: Selector is null or undefined');
            return;
        }
        try {
            const element = document.querySelector(selector); // Select element by the provided selector
            if (element) {
                element.style.removeProperty('display'); // Remove the display property to show the element
            } else {
                console.warn(`DOMUtils.removeDisplayFromElementBySelector: No element found for selector "${selector}"`);
            }
        } catch (error) {
            console.error(`DOMUtils.removeDisplayFromElementBySelector: Error while removing display from element for selector "${selector}":`, error);
        }
    }

    static removeClass(element, className) {
        if (!element) {
            console.warn('DOMUtils.removeClass: Element is null or undefined');
            return;
        }
        if (!className) {
            console.warn('DOMUtils.removeClass: className is null or undefined');
            return;
        }
        try {
            element.classList.remove(className);
        } catch (error) {
            console.error('DOMUtils.removeClass: Failed to remove class', error);
        }
    }


    static injectStylesheet(styles) {
        if (!styles) {
            console.warn('DOMUtils.addStyles: styles parameter is null or undefined');
            return;
        }
        try {
            const style = document.createElement('style');
            style.textContent = styles;
            document.head.appendChild(style);
        } catch (error) {
            console.error('DOMUtils.addStyles: Failed to add styles', error);
        }
    }

    static waitForElementWithTestId(testId, callback) {
        const checkExist = setInterval(() => {
            // Get the shadow host element
            const shadowHost = document.getElementById('app-embed'); // Adjust this to your shadow host ID or selector
            if (shadowHost && shadowHost.shadowRoot) {
                // Query the element inside the shadow DOM
                const element = shadowHost.shadowRoot.querySelector(`[data-testid="${testId}"]`);
                if (element) {
                    callback(element);
                    clearInterval(checkExist);
                }
            }
        }, 100);
    }

}